import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { addToContact } from 'APP/model/contact/contactModel';
import { markAsUnknown } from 'APP/model/user/userModel';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import type { User } from 'STORE/Users/User/User';

interface IUnknownUserPresenter {
  group: Group | null;
  opponentId: string | null;
  user: User | null;
  isShowUnknownUserBanner: boolean;
  isShowAddContact: boolean;
  onClose(): Promise<void>;
  onAddToContact(): Promise<void>;
}

export const useUnknownUserPresenter = (groupId: string): IUnknownUserPresenter => {
  const source = useAsObservableSource({ groupId });

  const presenter = useLocalStore<IUnknownUserPresenter>(() => ({
    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(source.groupId) || null;
    },

    get opponentId(): string | null {
      return presenter.group?.opponentId || null;
    },

    get user(): User | null {
      return presenter.opponentId ? Entities.usersStore.getUserById(presenter.opponentId) : null;
    },

    get isShowUnknownUserBanner(): boolean {
      if (!Features.isBanUserEnabled()) {
        return false;
      }
      return Boolean(presenter.user?.isUnknown);
    },

    get isShowAddContact(): boolean {
      return Features.isContactsEnabled();
    },

    async onAddToContact(): Promise<void> {
      if (!presenter.user) {
        return;
      }

      try {
        await addToContact({
          added: [
            {
              contactName: presenter.user.displayName,
              contactId: presenter.user.id,
            },
          ],
          last: true,
        });
      } catch (err: any) {
        //
      }
      presenter.user.setIsUnknown(false);
    },

    async onClose(): Promise<void> {
      if (!presenter.user) return;

      presenter.user.setIsUnknown(false);

      try {
        await markAsUnknown({ userId: presenter.user.id, isUnknown: false });
      } catch (err: any) {
        //
      }
    },
  }));

  return presenter;
};
